/* ─────────────────────────────────────────────────────────────
   Display Components — Read-only renderers for review/summary.
   ───────────────────────────────────────────────────────────── */

import type { FieldDisplayProps, ListBuilderConfig, ListBuilderSubField, RuntimeContext } from '../../config/types';
import { CURRENCIES } from './data';
import { usePlaceById, usePlaceByIsoCode } from '../../query/usePlacesOptions';
import { Text } from '@/components/ui/Text';

function DisplayValue({ children }: { children: React.ReactNode }) {
  return <Text variant="label-lg">{children}</Text>;
}

function EmptyValue() {
  return <Text variant="body-sm" color="subtle" as="span">&mdash;</Text>;
}

export function TextDisplay({ value }: FieldDisplayProps) {
  const str = value != null && value !== '' ? String(value) : '';
  if (!str) return <EmptyValue />;
  return <DisplayValue>{str}</DisplayValue>;
}

export function NumberDisplay({ value }: FieldDisplayProps) {
  if (value == null || value === '') return <EmptyValue />;
  return <DisplayValue>{String(value)}</DisplayValue>;
}

export function DateDisplay({ value }: FieldDisplayProps) {
  const str = value != null && value !== '' ? String(value) : '';
  if (!str) return <EmptyValue />;
  return <DisplayValue>{str}</DisplayValue>;
}

export function TextAreaDisplay({ value }: FieldDisplayProps) {
  const str = value != null && value !== '' ? String(value) : '';
  if (!str) return <EmptyValue />;
  return <DisplayValue>{str}</DisplayValue>;
}

function resolveSelectLabel(value: unknown, field: FieldDisplayProps['field']): string {
  const str = String(value ?? '');
  if (!str) return '';
  if (field.dataSource?.type === 'static') {
    const match = field.dataSource.options.find((o) => o.value === str);
    if (match) return match.label;
  }
  return str;
}

export function SelectDisplay({ field, value }: FieldDisplayProps) {
  // Places-backed selects store an opaque value (UUID / ISO code), not the
  // human name — resolve it so the Review screen shows "Singapore", not
  // "db1e2f97-ec4a-4016-b187-445721135b68" (FE-2).
  if (field.dataSource?.type === 'places') {
    return <PlacesValueDisplay field={field} value={value} />;
  }
  const label = resolveSelectLabel(value, field);
  if (!label) return <EmptyValue />;
  return <DisplayValue>{label}</DisplayValue>;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Read-only display for a places-backed field. Resolves the stored value to a
 * canonical place name based on the field's `valueField`:
 *   - 'id' (default) → UUID, resolved via usePlaceById
 *   - 'isoCode'      → e.g. "SG", resolved via usePlaceByIsoCode
 *   - 'canonicalName' → already a display name, shown as-is
 * Falls back to the raw value while the lookup is in flight or if it misses.
 *
 * UUID override: if the stored value is a UUID regardless of what valueField says,
 * use usePlaceById. Handles config/data drift where a field was re-configured from
 * valueField:'id' to 'isoCode' but existing saved sessions still contain a UUID.
 */
function PlacesValueDisplay({ field, value }: FieldDisplayProps) {
  const str = value != null && value !== '' ? String(value) : '';
  const valueField = field.dataSource?.type === 'places' ? field.dataSource.valueField : undefined;
  // If the stored value is a UUID, route to the UUID lookup regardless of valueField config.
  const looksLikeUuid = Boolean(str) && UUID_RE.test(str);
  const isIso = valueField === 'isoCode' && !looksLikeUuid;
  const isName = valueField === 'canonicalName' && !looksLikeUuid;
  const isUuid = !isIso && !isName; // 'id' or unset, or UUID stored despite isoCode/canonicalName config

  // Hooks must run unconditionally; each is enabled-gated internally.
  const { place: byId } = usePlaceById(isUuid && str ? str : null);
  const { place: byIso } = usePlaceByIsoCode(isIso && str ? str : null);

  if (!str) return <EmptyValue />;
  if (isName) return <DisplayValue>{str}</DisplayValue>;
  const resolved = isUuid ? byId : byIso;
  return <DisplayValue>{resolved?.canonicalName ?? str}</DisplayValue>;
}

export function CurrencySelectDisplay({ field, value }: FieldDisplayProps) {
  const str = String(value ?? '');
  if (!str) return <EmptyValue />;
  const label = resolveSelectLabel(value, field)
    || CURRENCIES.find((c) => c.value === str)?.label
    || str;
  return <DisplayValue>{label}</DisplayValue>;
}

export function CheckboxDisplay({ value }: FieldDisplayProps) {
  return <DisplayValue>{value ? 'Yes' : 'No'}</DisplayValue>;
}

/** Resolve a select-type subField's raw value to a human-readable label for display. */
function resolveSelectTitle(
  subField: ListBuilderSubField | undefined,
  rawValue: string,
  runtimeContext: RuntimeContext | undefined,
): string | null {
  if (!subField) return null;
  if (subField.inputType === 'currencySelect') {
    return CURRENCIES.find((o) => o.value === rawValue)?.label ?? null;
  }
  if (subField.inputType === 'select') {
    const ds = subField.dataSource;
    if (ds?.type === 'static') {
      return ds.options.find((o) => o.value === rawValue)?.label ?? null;
    }
    if (ds?.type === 'context' && runtimeContext) {
      const opts = (runtimeContext[ds.key] as { value: string; label: string }[] | undefined) ?? [];
      return opts.find((o) => o.value === rawValue)?.label ?? null;
    }
  }
  return null;
}

export function ListBuilderDisplay({ field, value, runtimeContext }: FieldDisplayProps) {
  const config = field.listBuilder;
  if (!config) return <EmptyValue />;

  const items = (value as Record<string, string>[] | undefined) ?? [];
  if (items.length === 0) return <EmptyValue />;

  return (
    <div className="w-full">
      {items.map((item, i) => (
        <ListItemDisplay key={i} item={item} config={config} isFirst={i === 0} runtimeContext={runtimeContext} />
      ))}
    </div>
  );
}

function ListItemDisplay({
  item,
  config,
  isFirst,
  runtimeContext,
}: {
  item: Record<string, string>;
  config: ListBuilderConfig;
  isFirst: boolean;
  runtimeContext?: RuntimeContext;
}) {
  const [titleSummaryField, ...detailFields] = config.summaryFields;
  const rawTitle = item[titleSummaryField?.field ?? ''];
  const titleSubField = config.subFields.find((sf) => sf.field === titleSummaryField?.field);
  const resolvedTitle = rawTitle
    ? (resolveSelectTitle(titleSubField, rawTitle, runtimeContext) ?? rawTitle)
    : null;
  // Prefer the summaryField's own label, then the matching subField's label, then the raw field name
  const titleLabel = titleSummaryField?.label ?? titleSubField?.label ?? titleSummaryField?.field;

  return (
    <div className={`${!isFirst ? 'mt-4 pt-4 border-t border-border-muted' : ''}`}>
      {titleSummaryField && resolvedTitle && (
        <div className="flex justify-between items-baseline gap-4 py-2 border-b border-border-muted last:border-0">
          <Text variant="label-sm" weight="medium" color="muted" className="shrink-0">{titleLabel}</Text>
          <Text variant="label-lg" className="text-right">{resolvedTitle}</Text>
        </div>
      )}
      {detailFields.map((sf) => {
        const val = item[sf.field];
        if (!val) return null;
        const label = sf.label ?? sf.field;
        return (
          <div key={sf.field} className="flex justify-between items-baseline gap-4 py-2 border-b border-border-muted last:border-0">
            <Text variant="label-sm" weight="medium" color="muted" className="shrink-0">{label}</Text>
            <Text variant="label-lg" className="text-right">{val}{sf.suffix ?? ''}</Text>
          </div>
        );
      })}
    </div>
  );
}
